import * as THREE from 'three';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { openModal } from '../../../components/common/Modal/openModal';
import ButtonSecondaryMedium from '../../../components/common/ButtonSecondary/ButtonSecondaryMedium';
import { roundTo } from '../../../utils/geometry/epsilon';
import { GeometryUtils } from '../../../utils/geometry/GeometryUtils';
import { InsideType } from '../../../constants/enum';

// Constants
const CONFIG = {
  POINT_COLOR: 0x00ff00,
  POINT_COLOR_SELECTED: 0xff0000,
  POINT_COLOR_HOVER: 0xffff00,
  POINT_SIZE: 0.005,
  LINE_COLOR: 0xff0000,
  LINE_WIDTH: 0.01,
  LABEL_FONT_SIZE: '10px',
  LABEL_FONT_WEIGHT: '500',
  LABEL_FONT_FAMILY:
    " 'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  LABEL_COLOR: '#000',
  LABEL_BG_COLOR: '#f2f2f2',
  LABEL_PADDING: '2px 6px',
  LABEL_BORDER_RADIUS: '3px',
  RENDER_ORDER: 100,
};

/**
 * Represents a corner point in 3D space
 */
class CornerPoint {
  constructor(position, moduleIndex, cornerType) {
    this.position = position.clone();
    this.moduleIndex = moduleIndex;
    this.cornerType = cornerType; // e.g., "top-left-front", "bottom-right-back"
    this.mesh = null;
    this.selected = false;
    this.id = `point_${moduleIndex}_${cornerType}`;
  }

  createMesh(config = CONFIG) {
    const geometry = new THREE.SphereGeometry(config.POINT_SIZE, 16, 16);
    const material = new THREE.MeshBasicMaterial({
      color: config.POINT_COLOR,
      transparent: true,
      opacity: 0,
      depthTest: false,
      depthWrite: false,
      renderOrder: 9999,
    });

    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.position.copy(this.position);
    this.mesh.userData.cornerPoint = this;
    this.mesh.renderOrder = config.RENDER_ORDER;

    return this.mesh;
  }

  setSelected(selected, config = CONFIG) {
    this.selected = selected;
    if (this.mesh) {
      this.mesh.material.color.setHex(
        selected ? config.POINT_COLOR_SELECTED : config.POINT_COLOR
      );
    }
  }

  setHover(hover, config = CONFIG) {
    if (this.mesh && !this.selected) {
      this.mesh.material.color.setHex(
        hover ? config.POINT_COLOR_HOVER : config.POINT_COLOR
      );
      this.mesh.material.opacity = hover ? 1.0 : 0.0;
    }
  }

  dispose() {
    if (this.mesh) {
      if (this.mesh.geometry) this.mesh.geometry.dispose();
      if (this.mesh.material) this.mesh.material.dispose();
    }
  }
}

/**
 * Represents a dimension line between two points
 */
class DimensionLine {
  constructor(point1, point2) {
    this.point1 = point1;
    this.point2 = point2;
    this.distance = this.calculateDistance();
    this.midpoint = this.calculateMidpoint();
    this.line = null;
    this.label = null;
    this.arrow1 = null;
    this.arrow2 = null;
    this.id = `line_${point1.id}_${point2.id}`;
  }

  calculateDistance() {
    return this.point1.position.distanceTo(this.point2.position);
  }

  calculateMidpoint() {
    return new THREE.Vector3(
      (this.point1.position.x + this.point2.position.x) / 2,
      (this.point1.position.y + this.point2.position.y) / 2,
      (this.point1.position.z + this.point2.position.z) / 2
    );
  }

  createLine(config = CONFIG) {
    const positions = [
      this.point1.position.x,
      this.point1.position.y,
      this.point1.position.z,
      this.point2.position.x,
      this.point2.position.y,
      this.point2.position.z,
    ];

    const geometry = new LineGeometry();
    geometry.setPositions(positions);

    const material = new LineMaterial({
      color: config.LINE_COLOR,
      linewidth: config.LINE_WIDTH,
      worldUnits: true,
    });

    this.line = new Line2(geometry, material);
    this.line.renderOrder = config.RENDER_ORDER;
    this.line.userData.dimensionLine = this;

    return this.line;
  }

  createLabel(config = CONFIG) {
    const div = document.createElement('div');
    div.className = 'dimension-label';
    div.style.fontSize = config.LABEL_FONT_SIZE;
    div.style.fontWeight = config.LABEL_FONT_WEIGHT;
    div.style.fontFamily = config.LABEL_FONT_FAMILY;
    div.style.color = config.LABEL_COLOR;
    div.style.backgroundColor = config.LABEL_BG_COLOR;
    div.style.padding = config.LABEL_PADDING;
    div.style.borderRadius = config.LABEL_BORDER_RADIUS;
    div.style.userSelect = 'none';
    div.style.pointerEvents = 'none';
    div.style.whiteSpace = 'nowrap';
    div.style.transition = 'opacity 0.2s ease';

    // Format distance in mm or m
    const distanceMM = this.distance * 1000; // assuming units are in meters
    // div.textContent =
    //   distanceMM >= 1000
    //     ? `${(distanceMM / 1000).toFixed(2)} m`
    //     : `${distanceMM.toFixed(0)} mm`;
    div.textContent = `${roundTo(distanceMM, 0)} mm`;

    this.label = new CSS2DObject(div);
    this.label.position.copy(this.midpoint);
    this.label.layers.set(0);
    this.label.renderOrder = config.RENDER_ORDER;

    return this.label;
  }

  createArrow1 = (start, end, color = 0xff0000) => {
    const dir = new THREE.Vector3()
      .subVectors(end.position, start.position)
      .normalize();
    const length = start.position.distanceTo(end.position);

    this.arrow1 = new THREE.ArrowHelper(
      dir,
      start.position,
      length,
      color,
      0.03,
      0.03
    );

    // Config để arrow luôn hiển thị trên các module khác
    this.arrow1.traverse(child => {
      if (child.isMesh) {
        child.material.depthTest = false;
        child.material.depthWrite = false;
        child.renderOrder = 9999;
      }
    });

    this.arrow1.renderOrder = 9999;

    return this.arrow1;
  };
  createArrow2 = (start, end, color = 0xff0000) => {
    const dir = new THREE.Vector3()
      .subVectors(end.position, start.position)
      .normalize();
    const length = start.position.distanceTo(end.position);

    this.arrow2 = new THREE.ArrowHelper(
      dir,
      start.position,
      length,
      color,
      0.03,
      0.03
    );

    // Config để arrow luôn hiển thị trên các module khác
    this.arrow2.traverse(child => {
      if (child.isMesh) {
        child.material.depthTest = false;
        child.material.depthWrite = false;
        child.renderOrder = 9999;
      }
    });

    this.arrow2.renderOrder = 9999;

    return this.arrow2;
  };

  dispose() {
    if (this.line) {
      if (this.line.geometry) this.line.geometry.dispose();
      if (this.line.material) this.line.material.dispose();
    }
    if (this.arrow1) {
      if (this.arrow1.geometry) this.arrow1.geometry.dispose();
      if (this.arrow1.material) this.arrow1.material.dispose();
    }
    if (this.arrow2) {
      if (this.arrow2.geometry) this.arrow2.geometry.dispose();
      if (this.arrow2.material) this.arrow2.material.dispose();
    }
    if (this.label && this.label.element) {
      this.label.element.remove();
    }
  }
}

/**
 * Main class to manage interactive dimension system
 */
class InteractiveDimensionSystem {
  constructor(camera, domElement, config = {}) {
    this.camera = camera;
    this.domElement = domElement;
    this.config = { ...CONFIG, ...config };

    this.cornerPoints = [];
    this.dimensionLines = [];
    this.selectedPoints = [];

    this.pointsGroup = new THREE.Group();
    this.pointsGroup.name = 'CornerPoints';

    this.linesGroup = new THREE.Group();
    this.linesGroup.name = 'DimensionLines';

    this.raycaster = new THREE.Raycaster();
    this.raycaster.params.Points.threshold = 0.02;
    this.raycaster.params.Line2 = { threshold: 0.02 }; // Thêm threshold cho Line2
    this.mouse = new THREE.Vector2();

    this.hoveredPoint = null;
    this.hoveredLine = null; // Thêm property này
    this.enabled = true;

    this.setupEventListeners();
  }

  /**
   * Generate corner points from list of modules
   */
  generateCornerPoints(scene, axis, showLabel) {
    // this.clearPoints();

    const modules = [];

    // Traverse through each mesh in module.group
    if (Array.isArray(scene)) {
      modules = scene;
    }
    if (scene?.isMesh) {
      modules.push(scene);
    } else {
      scene.traverse((child, index) => {
        // if (
        //   child.isGroup &&
        //   ['module'].some(item => child.userData.type.includes(item))
        // ) {
        // child.traverse((child, index) => {
        if (
          child.isMesh &&
          child.geometry &&
          child.name &&
          child.userData.type !== InsideType.HANDLE &&
          child.userData.isColumn !== true &&
          !['BEAM'].some(item => child.name.includes(item))
          // &&
          // !["G-MAT-DA", "G-VACH-BEP"].some((item) => child.name?.includes(item))
        ) {
          modules.push(child);
          const geometry = child.geometry;

          // Compute bounding box if not already computed
          if (!geometry.boundingBox) {
            geometry.computeBoundingBox();
          }

          const bbox = geometry.boundingBox.clone();

          // Apply world matrix to get correct position
          const worldMatrix = child.matrixWorld;
          bbox.applyMatrix4(worldMatrix);

          const corners = this.getBoxCorners(bbox);

          corners.forEach(corner => {
            const point = new CornerPoint(
              corner.position,
              index,
              `${child.name || child.uuid}_${corner.type}`
            );
            this.cornerPoints.push(point);

            const mesh = point.createMesh(this.config);
            this.pointsGroup.add(mesh);
          });
        }
        // });
        // }
      });
    }

    // });

    this.removeDuplicatePoints();
    this.createBoundingBoxDimensions(modules, axis, showLabel);
    return this.pointsGroup;
  }

  /**
   * Get all 8 corners of a bounding box
   */
  getBoxCorners(bbox) {
    const { min, max } = bbox;

    return [
      {
        position: new THREE.Vector3(min.x, min.y, min.z),
        type: 'bottom-left-front',
      },
      {
        position: new THREE.Vector3(max.x, min.y, min.z),
        type: 'bottom-right-front',
      },
      {
        position: new THREE.Vector3(min.x, max.y, min.z),
        type: 'top-left-front',
      },
      {
        position: new THREE.Vector3(max.x, max.y, min.z),
        type: 'top-right-front',
      },
      {
        position: new THREE.Vector3(min.x, min.y, max.z),
        type: 'bottom-left-back',
      },
      {
        position: new THREE.Vector3(max.x, min.y, max.z),
        type: 'bottom-right-back',
      },
      {
        position: new THREE.Vector3(min.x, max.y, max.z),
        type: 'top-left-back',
      },
      {
        position: new THREE.Vector3(max.x, max.y, max.z),
        type: 'top-right-back',
      },
    ];
  }

  /**
   * Remove duplicate corner points
   */
  removeDuplicatePoints(threshold = 0.001) {
    const uniquePoints = [];
    const duplicatePoints = [];

    for (let i = 0; i < this.cornerPoints.length; i++) {
      const currentPoint = this.cornerPoints[i];
      let isDuplicate = false;

      for (let j = 0; j < uniquePoints.length; j++) {
        const distance = currentPoint.position.distanceTo(
          uniquePoints[j].position
        );

        if (distance < threshold) {
          isDuplicate = true;
          duplicatePoints.push(currentPoint);
          break;
        }
      }

      if (!isDuplicate) {
        uniquePoints.push(currentPoint);
      }
    }

    // Remove duplicate meshes from scene and dispose
    duplicatePoints.forEach(point => {
      if (point.mesh) {
        this.pointsGroup.remove(point.mesh);
      }
      point.dispose();
    });

    this.cornerPoints = uniquePoints;

    return uniquePoints;
  }

  /**
   * Setup mouse event listeners
   */
  setupEventListeners() {
    this.onMouseMove = this.handleMouseMove.bind(this);
    this.onMouseClick = this.handleMouseClick.bind(this);
    this.onMouseDoubleClick = this.handleMouseDoubleClick.bind(this);

    this.domElement.addEventListener('mousemove', this.onMouseMove);
    this.domElement.addEventListener('click', this.onMouseClick);
    this.domElement.addEventListener('dblclick', this.onMouseDoubleClick);
  }

  /**
   * Handle mouse move for hover effects
   */
  handleMouseMove(event) {
    if (!this.enabled) return;

    this.updateMousePosition(event);

    const pointIntersects = this.raycaster.intersectObjects(
      this.pointsGroup.children
    );

    // Check intersection with lines
    const lineIntersects = this.raycaster.intersectObjects(
      this.linesGroup.children.filter(child => child.isLine2)
    );

    // Reset previous hover
    if (
      this.hoveredPoint &&
      (!pointIntersects.length ||
        pointIntersects[0].object !== this.hoveredPoint.mesh)
    ) {
      this.hoveredPoint.setHover(false, this.config);
      this.hoveredPoint = null;
    }

    // Reset line hover
    if (this.hoveredLine) {
      this.hoveredLine = null;
    }

    // Set cursor based on what's hovered
    if (pointIntersects.length > 0) {
      const point = pointIntersects[0].object.userData.cornerPoint;
      if (point && point !== this.hoveredPoint) {
        this.hoveredPoint = point;
        point.setHover(true, this.config);
        this.domElement.style.cursor = 'pointer';
        this.setLinesVisible(false);
      }
    } else {
      this.setLinesVisible(true);

      if (lineIntersects.length > 0) {
        // Hover over line
        this.hoveredLine = lineIntersects[0].object;
        this.domElement.style.cursor = 'pointer';
      } else {
        this.domElement.style.cursor = 'default';
      }
    }
  }

  /**
   * Handle mouse click to select points and create lines
   */
  handleMouseClick(event) {
    if (!this.enabled) return;

    this.updateMousePosition(event);

    // Check if clicking on a point
    const pointIntersects = this.raycaster.intersectObjects(
      this.pointsGroup.children
    );

    if (pointIntersects.length > 0) {
      const clickedPoint = pointIntersects[0].object.userData.cornerPoint;

      if (!clickedPoint) return;

      // Toggle selection
      if (this.selectedPoints.includes(clickedPoint)) {
        this.deselectPoint(clickedPoint);
      } else {
        this.selectPoint(clickedPoint);
      }

      // If we have 2 selected points, create a line
      if (this.selectedPoints.length === 2) {
        this.createDimensionLine(
          this.selectedPoints[0],
          this.selectedPoints[1]
        );
        this.clearSelection();
      }
    }
  }

  handleMouseDoubleClick(event) {
    if (!this.enabled) return;

    this.updateMousePosition(event);

    // Check if clicking on a line first
    const lineIntersects = this.raycaster.intersectObjects(
      this.linesGroup.children.filter(child => child.isLine2)
    );

    if (lineIntersects.length > 0 && this.linesGroup.visible) {
      const clickedLine = lineIntersects[0].object;
      const dimensionLine = clickedLine.userData.dimensionLine;

      if (dimensionLine) {
        // Optional: Add confirmation
        const distanceMM = (dimensionLine.distance * 1000).toFixed(0);
        const confirmDelete = openModal({
          title: (
            <div className="span title-large emphasized">{`Xóa khoảng cách (${distanceMM} mm)?`}</div>
          ),
          centered: true,
          width: 538,
          classNames: {
            header: '!px-16 !py-12 gap-12 !m-0',
            body: '!p-0 !m-0 self-stretch',
            iconClose: 'absolute top-12 right-16',
            footer: '!px-16 !py-12 gap-16 items-end flex justify-end !m-0',
          },
          footer: (
            <>
              <ButtonSecondaryMedium
                title={'Hủy bỏ'}
                onClick={() => {
                  confirmDelete.destroy();
                }}
              />
              <ButtonSecondaryMedium
                title={'Xóa ngay'}
                onClick={() => {
                  confirmDelete.destroy();
                  if (confirmDelete) {
                    this.removeDimensionLine(dimensionLine);
                    // console.log(`Deleted dimension line: ${dimensionLine.id}`);
                  }
                }}
              />
            </>
          ),
        });

        if (confirmDelete) {
          this.removeDimensionLine(dimensionLine);
          // console.log(`Deleted dimension line: ${dimensionLine.id}`);
        }
        return; // Don't process point selection
      }
    }
  }

  /**
   * Update mouse position for raycasting
   */
  updateMousePosition(event) {
    const rect = this.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.camera);
  }

  /**
   * Select a point
   */
  selectPoint(point) {
    if (this.selectedPoints.length >= 2) {
      this.clearSelection();
    }

    this.selectedPoints.push(point);
    point.setSelected(true, this.config);

    // console.log(
    //   `Selected point: ${point.id} (${this.selectedPoints.length}/2)`
    // );
  }

  /**
   * Deselect a point
   */
  deselectPoint(point) {
    const index = this.selectedPoints.indexOf(point);
    if (index > -1) {
      this.selectedPoints.splice(index, 1);
      point.setSelected(false, this.config);
      // console.log(`Deselected point: ${point.id}`);
    }
  }

  /**
   * Clear all selected points
   */
  clearSelection() {
    this.selectedPoints.forEach(point => {
      point.setSelected(false, this.config);
    });
    this.selectedPoints = [];
  }

  /**
   * Create a dimension line between two points
   */
  createDimensionLine(point1, point2, showLabel) {
    const dimLine = new DimensionLine(point1, point2);
    this.dimensionLines.push(dimLine);

    const line = dimLine.createLine(this.config);

    if (showLabel !== false) {
      const label = dimLine.createLabel(this.config);
      this.linesGroup.add(label);
    }
    const arrow1 = dimLine.createArrow1(point1, point2);
    const arrow2 = dimLine.createArrow2(point2, point1);

    this.linesGroup.add(line);
    this.linesGroup.add(arrow1);
    this.linesGroup.add(arrow2);

    // console.log(`Created dimension line: ${dimLine.distance.toFixed(3)} units`);

    return dimLine;
  }

  /**
   * Remove a dimension line
   */
  removeDimensionLine(dimensionLine) {
    const index = this.dimensionLines.indexOf(dimensionLine);
    if (index > -1) {
      this.dimensionLines.splice(index, 1);

      if (dimensionLine.line) this.linesGroup.remove(dimensionLine.line);
      if (dimensionLine.label) this.linesGroup.remove(dimensionLine.label);
      if (dimensionLine.arrow1) this.linesGroup.remove(dimensionLine.arrow1);
      if (dimensionLine.arrow2) this.linesGroup.remove(dimensionLine.arrow2);

      dimensionLine.dispose();
    }
  }

  /**
   * Clear all dimension lines
   */
  clearLines() {
    this.dimensionLines.forEach(line => {
      if (line.line) this.linesGroup.remove(line.line);
      if (line.label) this.linesGroup.remove(line.label);
      if (line.arrow1) this.linesGroup.remove(line.arrow1);
      if (line.arrow2) this.linesGroup.remove(line.arrow2);
      line.dispose();
    });
    this.dimensionLines = [];
  }

  /**
   * Clear all corner points
   */
  clearPoints() {
    this.clearSelection();
    this.cornerPoints.forEach(point => {
      if (point.mesh) this.pointsGroup.remove(point.mesh);
      point.dispose();
    });
    this.cornerPoints = [];
  }

  /**
   * Clear everything
   */
  clearAll() {
    this.clearLines();
    this.clearPoints();
  }

  /**
   * Set points visibility
   */
  setPointsVisible(visible) {
    this.pointsGroup.visible = visible;
  }

  /**
   * Set lines visibility with smooth transition
   */
  setLinesVisible(visible) {
    this.linesGroup.visible = visible;

    // Toggle visibility for all dimension labels with transition
    this.dimensionLines.forEach(dimensionLine => {
      if (dimensionLine.label && dimensionLine.label.element) {
        const element = dimensionLine.label.element;

        if (visible) {
          element.style.display = 'block';
          element.style.opacity = '1';
        } else {
          element.style.opacity = '0';
          // Hide after transition completes
          setTimeout(() => {
            if (element.style.opacity === '0') {
              element.style.display = 'none';
            }
          }, 200);
        }
      }
    });
  }

  /**
   * Enable/disable interaction
   */
  setEnabled(enabled) {
    this.enabled = enabled;
    if (!enabled) {
      this.clearSelection();
      if (this.hoveredPoint) {
        this.hoveredPoint.setHover(false, this.config);
        this.hoveredPoint = null;
      }
      this.domElement.style.cursor = 'default';
    }
  }

  /**
   * Add to scene
   */
  addToScene(scene) {
    scene.add(this.pointsGroup);
    scene.add(this.linesGroup);
  }

  /**
   * Remove from scene
   */
  removeFromScene(scene) {
    scene.remove(this.pointsGroup);
    scene.remove(this.linesGroup);
  }

  /**
   * Get all dimension data
   */
  getDimensionData() {
    return this.dimensionLines.map(line => ({
      id: line.id,
      point1: {
        id: line.point1.id,
        position: line.point1.position,
        cornerType: line.point1.cornerType,
      },
      point2: {
        id: line.point2.id,
        position: line.point2.position,
        cornerType: line.point2.cornerType,
      },
      distance: line.distance,
    }));
  }

  /**
   * Dispose and cleanup
   */
  dispose() {
    this.domElement.removeEventListener('mousemove', this.onMouseMove);
    this.domElement.removeEventListener('click', this.onMouseClick);

    this.clearAll();
    this.pointsGroup = null;
    this.linesGroup = null;
    this.raycaster = null;
  }

  /**
   * Calculate the overall bounding box of all modules
   * @param {Array} modules - Array of modules
   * @returns {THREE.Box3} Combined bounding box
   */
  calculateModulesBoundingBox(modules) {
    const overallBBox = new THREE.Box3();

    modules.forEach((module, index) => {
      if (!module) {
        // console.warn(`Module at index ${index} has no group`);
        return;
      }

      // Create a box for this module
      const moduleBBox = new THREE.Box3();

      module.traverse(child => {
        if (child.isMesh && child.geometry) {
          const geometry = child.geometry;

          if (!geometry.boundingBox) {
            geometry.computeBoundingBox();
          }

          const bbox = geometry.boundingBox.clone();
          bbox.applyMatrix4(child.matrixWorld);

          moduleBBox.union(bbox);
        }
      });

      // Add this module's bbox to overall bbox
      overallBBox.union(moduleBBox);
    });

    return overallBBox;
  }

  /**
   * Find or create corner points at the 8 corners of the overall bounding box
   * @param {THREE.Box3} bbox - Bounding box
   * @returns {Array} Array of 8 corner points
   */
  getBoundingBoxCornerPoints(bbox) {
    const { min, max } = bbox;

    const bboxCorners = [
      {
        position: new THREE.Vector3(min.x, min.y, min.z),
        type: 'min-min-min',
      },
      {
        position: new THREE.Vector3(max.x, min.y, min.z),
        type: 'max-min-min',
      },
      {
        position: new THREE.Vector3(min.x, max.y, min.z),
        type: 'min-max-min',
      },
      {
        position: new THREE.Vector3(max.x, max.y, min.z),
        type: 'max-max-min',
      },
      {
        position: new THREE.Vector3(min.x, min.y, max.z),
        type: 'min-min-max',
      },
      {
        position: new THREE.Vector3(max.x, min.y, max.z),
        type: 'max-min-max',
      },
      {
        position: new THREE.Vector3(min.x, max.y, max.z),
        type: 'min-max-max',
      },
      {
        position: new THREE.Vector3(max.x, max.y, max.z),
        type: 'max-max-max',
      },
    ];

    const cornerPoints = [];
    const threshold = 0.001; // 1mm threshold

    bboxCorners.forEach((corner, index) => {
      // Try to find existing point near this corner
      const existingPoint = this.cornerPoints.find(
        p => p.position.distanceTo(corner.position) < threshold
      );

      if (existingPoint) {
        cornerPoints.push(existingPoint);
      } else {
        // Create new point at this corner
        const newPoint = new CornerPoint(
          corner.position,
          -1, // Special index for bbox corners
          `bbox_${corner.type}`
        );

        // // Add to corner points array
        // this.cornerPoints.push(newPoint);

        // // Create and add mesh
        // const mesh = newPoint.createMesh(this.config);
        // this.pointsGroup.add(mesh);

        cornerPoints.push(newPoint);
      }
    });

    return cornerPoints;
  }

  /**
   * Create 3 perpendicular dimension lines from a corner of the overall bounding box
   * @param {Array} modules - Array of modules
   * @param {string} cornerType - Which corner to use: 'min' (default), 'max', 'center'
   * @returns {Object} Created lines and corner info
   */
  createBoundingBoxDimensions(modules, axis, showLabel) {
    // Calculate overall bounding box
    const bbox = this.calculateModulesBoundingBox(modules);
    const size = bbox.getSize(new THREE.Vector3());

    // Get all 8 corner points
    const bboxCorners = this.getBoundingBoxCornerPoints(bbox);

    // Select corner point based on cornerType
    let cornerPoint;
    let xPoint, yPoint, zPoint;

    cornerPoint = bboxCorners[0];
    xPoint = bboxCorners[1];
    yPoint = bboxCorners[2];
    zPoint = bboxCorners[4];

    // // Highlight the corner point
    // if (cornerPoint.mesh) {
    //   cornerPoint.mesh.material.color.setHex(0xff00ff); // Magenta
    //   cornerPoint.mesh.scale.setScalar(2.5);
    // }

    const createdLines = [];
    const createdArrows = [];

    // Create X-axis line (Red)
    if (['X', 'x'].some(item => axis?.includes(item)) || !axis) {
      const xLine = this.createDimensionLine(cornerPoint, xPoint, showLabel);
      xLine.axisType = 'X';
      xLine.isBoundingBoxLine = true;
      if (xLine.line) {
        xLine.line.material.color.setHex(0xff0000);
        xLine.line.material.linewidth = this.config.LINE_WIDTH;
        xLine.line.material.depthTest = false;
        xLine.line.material.depthWrite = false;
        xLine.line.renderOrder = 9999;
      }
      if (xLine.label && xLine.label.element) {
        xLine.label.element.style.fontWeight = '600';
      }
      createdLines.push(xLine);
    }

    // Create Y-axis line (Green)
    if (['Y', 'y'].some(item => axis?.includes(item)) || !axis) {
      const yLine = this.createDimensionLine(cornerPoint, yPoint, showLabel);
      yLine.axisType = 'Y';
      yLine.isBoundingBoxLine = true;
      if (yLine.line) {
        yLine.line.material.color.setHex(0xff0000);
        yLine.line.material.linewidth = this.config.LINE_WIDTH;
        yLine.line.material.depthTest = false;
        yLine.line.material.depthWrite = false;
        yLine.line.renderOrder = 9999;
      }
      if (yLine.label && yLine.label.element) {
        yLine.label.element.style.fontWeight = '600';
      }
      createdLines.push(yLine);
    }

    // Create Z-axis line (Blue)
    if (['Z', 'z'].some(item => axis?.includes(item)) || !axis) {
      const zLine = this.createDimensionLine(cornerPoint, zPoint, showLabel);
      zLine.axisType = 'Z';
      zLine.isBoundingBoxLine = true;
      if (zLine.line) {
        zLine.line.material.color.setHex(0xff0000);
        zLine.line.material.linewidth = this.config.LINE_WIDTH;
        zLine.line.material.depthTest = false;
        zLine.line.material.depthWrite = false;
        zLine.line.renderOrder = 9999;
      }
      if (zLine.label && zLine.label.element) {
        zLine.label.element.style.fontWeight = '600';
      }
      createdLines.push(zLine);
    }
    return {
      lines: createdLines,
      arrows: createdArrows,
      corner: cornerPoint,
      bbox: bbox,
      dimensions: {
        x: size.x,
        y: size.y,
        z: size.z,
        volume: size.x * size.y * size.z,
      },
    };
  }

  /**
   * Visualize the overall bounding box with a wireframe
   * @param {THREE.Box3} bbox - Bounding box to visualize
   * @param {THREE.Scene} scene - Scene to add the visualization to
   * @returns {THREE.LineSegments} The wireframe mesh
   */
  visualizeBoundingBox(bbox, scene) {
    const geometry = new THREE.BoxGeometry(
      bbox.max.x - bbox.min.x,
      bbox.max.y - bbox.min.y,
      bbox.max.z - bbox.min.z
    );

    const edges = new THREE.EdgesGeometry(geometry);
    const material = new THREE.LineBasicMaterial({
      color: 0xffff00,
      linewidth: 0.5,
      transparent: true,
      opacity: 0.5,
    });

    const wireframe = new THREE.LineSegments(edges, material);

    const center = bbox.getCenter(new THREE.Vector3());
    wireframe.position.copy(center);
    wireframe.name = 'BoundingBoxWireframe';

    scene.add(wireframe);

    return wireframe;
  }

  /**
   * Remove bounding box visualization
   * @param {THREE.Scene} scene - Scene to remove from
   */
  removeBoundingBoxVisualization(scene) {
    const wireframe = scene.getObjectByName('BoundingBoxWireframe');
    if (wireframe) {
      scene.remove(wireframe);
      wireframe.geometry.dispose();
      wireframe.material.dispose();
    }
  }
}

export { InteractiveDimensionSystem, CornerPoint, DimensionLine };
